import React, { useRef, useState } from "react";
import RegenerateDropdown from "../../Dropdowns/RegenerateDropdown";
import CopyButton from "../../Button/CopyButton";
import { NextIcon, PreviousIcon } from "../../../assets";
import UserAuthStore from "../../../store/userStore";
import { useExportMessage } from "../../../api/queryHooks";
import { useMermaidSvgRegistry } from "../../../context/MermaidSvgRegistryContext";
import MessagesActionDropdown from "../../Dropdowns/MessageActionDropdown/MessagesActionDropdown";
import ModelBadge from "../Model/ModelBadge";
import logger from "../../../services/Logger";
import mediaUrlAdapter from "../../../api/Media/mediaUrlAdapter";
import { Download, FileText, CalendarClock } from "lucide-react";
import InlineSpinner from "../Spinner/InlineSpinner";
import useClickOutside from "../../../hooks/useClickOutside";
import CustomTooltip from "../../Tooltip/CustomTooltip";
import { useSparkContext } from "../../../context/SparkContext";
/**
 * MessageFooter - Standard footer for AI-generated message responses
 * Shows copy, edit/delete, regenerate, navigation, and model badge
 * 
 * @param {Function} regenFunc - Function to regenerate the message
 * @param {Function} handlePrevious - Navigate to previous answer
 * @param {Function} handleNext - Navigate to next answer
 * @param {boolean} disablePrevious - Whether previous button is disabled
 * @param {boolean} disableNext - Whether next button is disabled
 * @param {number} currentIndex - Current answer index
 * @param {number} totalAnswers - Total number of answers
 * @param {string} content - Message content for copy
 * @param {Array} files - Attached files
 * @param {number} regenerateIndex - Index of message being regenerated
 * @param {Function} setRegenerateIndex - Set regenerate index
 * @param {number} index - Message index
 * @param {string} modelSelected - Selected model ID
 * @param {Object} chat - Chat message object
 * @param {string} chat_id - Chat ID
 * @param {Function} deleteMessage - Function to delete message
 * @param {Object} space - Space object
 * @param {Object} agent - Agent object (if applicable)
 * @param {string} agent_id - Agent ID (if applicable)
 * @param {boolean} hideRegenerate - Hide regeneration for terminal states that must not be retried
 * @param {boolean} isLatest - Whether this is the latest turn in the conversation
 */
const MessageFooter = ({
  regenFunc,
  handlePrevious,
  handleNext,
  disablePrevious,
  disableNext,
  currentIndex,
  totalAnswers,
  content,
  files,
  regenerateIndex,
  setRegenerateIndex,
  index,
  modelSelected,
  chat,
  deleteMessage,
  agent,
  agent_id,
  leadingAction,
  hideRegenerate = false,
  isLatest = true,
}) => {
  const { chat_id, space } = useSparkContext();
  const { user } = UserAuthStore.getState();
  const orgId = UserAuthStore.getState().orgId;
  const { mutateAsync: exportMessage } = useExportMessage();
  const registry = useMermaidSvgRegistry();
  const [exportingFormat, setExportingFormat] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const currentPromptId = chat.answers?.[currentIndex]?.prompt_id || chat.id;
  useClickOutside(exportMenuRef, () => !exportingFormat && setShowExportMenu(false));

  const handleExport = async (format) => {
    if (exportingFormat) return;
    setExportingFormat(format);
    try {
      const diagrams = registry ? registry.getDiagrams() : {};
      const result = await exportMessage({
        content,
        format,
        diagrams,
        org_id: orgId,
        user_id: space?.owner_id ? space?.owner_id : user?.uid,
        owner_id: space?.owner_id || user?.uid,
        space_id: space?.space_id || null,
        chat_id,
        prompt_id: currentPromptId,
      });
      // Resolve GCS path to a URL, then force-download via blob anchor
      if (result?.file_path) {
        const downloadUrl = await mediaUrlAdapter.getUrl(result.file_path, orgId);
        if (downloadUrl && downloadUrl !== "ERROR") {
          const blob = await fetch(downloadUrl).then((r) => r.blob());
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = result.file_path.split("/").pop();
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
        } else {
          logger.error("MessageFooter", "Failed to resolve download URL", { file_path: result.file_path });
        }
      }
    } catch (err) {
      logger.error("MessageFooter", "Export failed", { error: err?.message, format });
    } finally {
      setExportingFormat(null);
      setShowExportMenu(false);
    }
  };

  // Filter out answers awaiting approval from pagination
  const visibleAnswers = chat.answers?.filter(a => !a.awaiting_approval) || [];
  const visibleCurrentIndex = visibleAnswers.findIndex(
    (answer) => answer.id === chat.answers?.[currentIndex]?.id
  );

  const navigate = (direction) => {
    direction === "next" ? handleNext() : handlePrevious();
  };

  return (
    <div className="flex justify-between items-center w-full gap-2 sm:gap-0 my-2 mt-2">
      <div className={`cursor-pointer flex items-center ${leadingAction ? "h-11" : "h-[32px]"}`}>
        {leadingAction}
        <CopyButton text={content} />

        {/* Export dropdown */}
        <div className="relative" ref={exportMenuRef}>
          <CustomTooltip content="Export message">
            <button
              type="button"
              onClick={() => !exportingFormat && setShowExportMenu((p) => !p)}
              aria-label="Export message"
              aria-haspopup="menu"
              aria-expanded={showExportMenu}
              disabled={Boolean(exportingFormat)}
              className="p-2 rounded-full hover:bg-fill-icon-hover/25 dark:hover:bg-fill-icon-hover-dark/100 transition-all duration-200 group"
            >
              {exportingFormat
                ? <InlineSpinner size="sm" className="text-ink-ink dark:text-ink-white" />
                : <Download className="w-4 h-4 text-ink-ink dark:text-ink-white group-hover:text-ink-gray-700 dark:group-hover:text-ink-gray-200 transition-colors duration-200" />}
            </button>
          </CustomTooltip>
          {showExportMenu && (
            <div role="menu" className="absolute bottom-full left-2 w-fit min-w-[150px] px-2 py-1.5 border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-panel-surface shadow-lg backdrop-blur-sm rounded-lg z-10">
              <button
                type="button"
                role="menuitem"
                disabled={Boolean(exportingFormat)}
                onClick={!exportingFormat ? () => { setShowExportMenu(false); handleExport("pdf"); } : undefined}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-[12px] text-ink-ink dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-editor-surface rounded-lg transition-all duration-200 ${exportingFormat ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {exportingFormat === "pdf"
                  ? <InlineSpinner size="sm" className="text-ink-red-600 dark:text-ink-red-400" />
                  : <FileText className="w-3.5 h-3.5 text-ink-red-600 dark:text-ink-red-400" />}
                <span className="font-medium whitespace-nowrap">{exportingFormat === "pdf" ? "Exporting..." : "Export as PDF"}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={Boolean(exportingFormat)}
                onClick={!exportingFormat ? () => { setShowExportMenu(false); handleExport("docx"); } : undefined}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-[12px] text-ink-ink dark:text-ink-gray-200 hover:bg-fill-gray-100 dark:hover:bg-fill-editor-surface rounded-lg transition-all duration-200 ${exportingFormat ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {exportingFormat === "docx"
                  ? <InlineSpinner size="sm" className="text-ink-blue-600 dark:text-ink-blue-300" />
                  : <FileText className="w-3.5 h-3.5 text-ink-blue-600 dark:text-ink-blue-300" />}
                <span className="font-medium whitespace-nowrap">{exportingFormat === "docx" ? "Exporting..." : "Export as DOCX"}</span>
              </button>
            </div>
          )}
        </div>

        <MessagesActionDropdown
          prompt_id={currentPromptId}
          chat_id={chat_id}
          deleteMessage={deleteMessage}
          space={space}
          answer_id={chat.answers?.[currentIndex]?.id}
          isLatest={isLatest}
        />
        {agent?.name || hideRegenerate ? null : (
          <>
            {visibleAnswers.length > 0 ? (
                <RegenerateDropdown
                  chatId={chat.id}
                  onRegenerate={regenFunc}
                  files={files}
                  regenerateIndex={regenerateIndex}
                  setRegenerateIndex={setRegenerateIndex}
                  index={index}
                  modelSelected={modelSelected}
                  styleId={chat.answers?.[currentIndex]?.style_id}
                  styleVersionId={chat.answers?.[currentIndex]?.style_version_id}
                  styleSelectionMode={chat.answers?.[currentIndex]?.style_selection_mode}
                  attachments={chat.attachments || []}
                />
            ) : null}
            <div
              className={`flex items-center gap-1.5 sm:gap-2 ${
                visibleAnswers.length > 1 ? "" : "invisible"
              }`}
            >
              <button
                type="button"
                aria-label="Previous response"
                className={`dark:text-ink-white p-0.5 sm:p-0 ${
                  disablePrevious
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:text-ink-icon-hover dark:hover:text-ink-gray-300"
                }`}
                onClick={() => navigate("prev")}
                disabled={disablePrevious}
              >
                <PreviousIcon className="w-3 h-3" />
              </button>

              <span className="dark:text-ink-white font-medium text-xs">
                {visibleCurrentIndex + 1}/{visibleAnswers.length}
              </span>

              <button
                type="button"
                aria-label="Next response"
                className={`dark:text-ink-white p-0.5 sm:p-0 ${
                  disableNext
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:text-ink-icon-hover dark:hover:text-ink-gray-300"
                }`}
                onClick={() => navigate("next")}
                disabled={disableNext}
              >
                <NextIcon className="w-3 h-3" />
              </button>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Scheduled badge — shown when this response was triggered by a scheduler job */}
        {(() => {
          const tb = chat?.answers?.[currentIndex]?.triggered_by || chat?.triggered_by;
          if (!tb) return null;
          const jobId = String(tb).startsWith("scheduler:") ? String(tb).slice(10) : null;
          return (
            <CustomTooltip content={jobId ? `Scheduled · ${jobId}` : "Generated by a scheduled job"}>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-fill-indigo-50 dark:bg-fill-hex-2d3557 text-ink-hex-3b5bdb dark:text-ink-blue-300 text-[11px] font-medium">
                <CalendarClock className="w-3 h-3" />
                <span>Scheduled</span>
              </div>
            </CustomTooltip>
          );
        })()}
        <ModelBadge 
          model_id={chat?.answers?.[currentIndex]?.model_id}
          modelDbName={chat?.answers?.[currentIndex]?.model_name}
          currentIndex={currentIndex}
          usage={chat?.answers?.[currentIndex]?.usage}
        />
      </div>
    </div>
  );
};

export default MessageFooter;
